import React, { useRef, useState } from "react";
import DiscountModal from "./DiscountModal";

interface SaleLine {
  id: string;
  product_id: string;
  sellable_format_id: string;
  qty: number;
  oz_total: number;
  cash_price_at_sale: number;
  card_price_at_sale: number;
  discount_applied: number;
  discount_type: string;
  discount_value: number;
  discount_reason: string;
  product_name: string;
  format_name: string;
  package_type: string;
}

interface CartDisplayProps {
  lines: SaleLine[];
  onUpdateQty: (lineId: string, qty: number) => void;
  onRemove: (lineId: string) => void;
  onLineDiscount?: (lineId: string, type: "percent_off" | "dollar_off", value: number, reason: string, reasonType?: string) => void;
  onRemoveLineDiscount?: (lineId: string) => void;
  isManager?: boolean;
}

// Donovan 10/29: + and - one at a time is slow for 18 cans. Tap the number,
// type it, Enter. Whole units of the line's format (single, 4pk, growler...),
// so there are no fractions of a case to work out the way mPower needed.
// Anything over 999 is treated as a stray barcode scan and ignored.
function QtyInput({ qty, onCommit }: { qty: number; onCommit: (qty: number) => void }) {
  const [text, setText] = useState<string | null>(null);
  const cancelled = useRef(false);
  const commit = () => {
    if (text === null || cancelled.current) { cancelled.current = false; setText(null); return; }
    const n = Number(text);
    setText(null);
    if (Number.isInteger(n) && n >= 0 && n <= 999 && n !== qty) onCommit(n);
  };
  return (
    <input
      value={text ?? String(qty)}
      inputMode="numeric"
      aria-label="Quantity"
      onFocus={(e) => { setText(String(qty)); e.currentTarget.select(); }}
      onChange={(e) => setText(e.target.value.replace(/[^0-9]/g, ""))}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); (e.currentTarget as HTMLInputElement).blur(); }
        if (e.key === "Escape") { cancelled.current = true; (e.currentTarget as HTMLInputElement).blur(); }
      }}
      onBlur={commit}
      className="w-12 text-center font-bold text-lg rounded-md"
      style={{ color: "var(--rbw-navy)", background: "transparent", border: "1px solid var(--rbw-border)" }}
    />
  );
}

export default function CartDisplay({ lines, onUpdateQty, onRemove, onLineDiscount, onRemoveLineDiscount, isManager }: CartDisplayProps) {
  const [discountLine, setDiscountLine] = useState<SaleLine | null>(null);
  if (lines.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center" style={{ color: "var(--rbw-gray)" }}>
        <div className="text-center">
          <img src="/images/logo-wordmark.jpg" alt="" className="w-32 mx-auto mb-4 opacity-30 rounded" />
          <div className="text-lg font-medium">Scan or search to add items</div>
          <div className="text-sm mt-1">Barcode scanner or type to search</div>
        </div>
      </div>
    );
  }

  const handleDiscountConfirm = (type: "percent_off" | "dollar_off", value: number, reason: string, reasonType?: string) => {
    if (discountLine && onLineDiscount) {
      onLineDiscount(discountLine.id, type, value, reason, reasonType);
    }
    setDiscountLine(null);
  };

  const handleRemoveDiscount = () => {
    if (discountLine && onRemoveLineDiscount) {
      onRemoveLineDiscount(discountLine.id);
    }
    setDiscountLine(null);
  };

  const formatDiscountLabel = (line: SaleLine): string => {
    const type = line.discount_type;
    const val = Number(line.discount_value);
    const applied = Number(line.discount_applied);
    if (type === "percent_off") {
      return `-${val}% (-$${applied.toFixed(2)})`;
    }
    return `-$${applied.toFixed(2)}`;
  };

  return (
    <div className="flex-1 overflow-y-auto">
      {discountLine && (
        <DiscountModal
          title="Line Discount"
          subtitle={discountLine.product_name}
          onConfirm={handleDiscountConfirm}
          onClose={() => setDiscountLine(null)}
          onRemove={handleRemoveDiscount}
          hasExistingDiscount={Number(discountLine.discount_applied) > 0}
        />
      )}
      <table className="w-full">
        <thead style={{ background: "var(--rbw-gray-light)" }}>
          <tr className="text-xs uppercase tracking-wider" style={{ color: "var(--rbw-gray-dark)" }}>
            <th className="text-left px-4 py-2.5">Item</th>
            <th className="text-center px-2 py-2.5 w-28">Qty</th>
            <th className="text-right px-3 py-2.5 w-24">
              <span style={{ color: "var(--rbw-navy)" }}>Cash</span>
            </th>
            <th className="text-right px-3 py-2.5 w-24">
              <span style={{ color: "var(--rbw-blue-dark)" }}>Card</span>
            </th>
            <th className="w-10"></th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, idx) => {
            const cashTotal = Number(line.cash_price_at_sale) * line.qty - Number(line.discount_applied);
            const hasDiscount = Number(line.discount_applied) > 0;

            return (
              <tr
                key={line.id}
                style={{
                  borderBottom: "1px solid var(--rbw-border)",
                  background: idx % 2 === 0 ? "transparent" : "var(--rbw-bg)",
                }}
              >
                <td className="px-4 py-2.5">
                  <div className="font-semibold text-sm" style={{ color: "var(--rbw-navy)" }}>
                    {line.product_name}
                  </div>
                  <div className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>{line.format_name}</div>
                  {hasDiscount && (
                    <div className="text-xs font-medium" style={{ color: "var(--rbw-danger)" }}>
                      {formatDiscountLabel(line)}
                      {line.discount_reason && (
                        <span style={{ color: "var(--rbw-gray-dark)" }}> &middot; {line.discount_reason}</span>
                      )}
                    </div>
                  )}
                </td>
                <td className="text-center px-2 py-2.5">
                  <div className="flex items-center justify-center gap-1">
                    <button
                      onClick={() => onUpdateQty(line.id, line.qty - 1)}
                      className="w-8 h-8 rounded-lg text-lg font-bold flex items-center justify-center transition-all active:scale-90"
                      style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }}
                    >
                      -
                    </button>
                    <QtyInput qty={line.qty} onCommit={(q) => onUpdateQty(line.id, q)} />
                    <button
                      onClick={() => onUpdateQty(line.id, line.qty + 1)}
                      className="w-8 h-8 rounded-lg text-lg font-bold flex items-center justify-center transition-all active:scale-90"
                      style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }}
                    >
                      +
                    </button>
                  </div>
                </td>
                <td className="text-right px-3 py-2.5 font-bold" style={{ color: "var(--rbw-navy)" }}>
                  ${cashTotal.toFixed(2)}
                </td>
                <td className="text-right px-3 py-2.5 font-medium" style={{ color: "var(--rbw-blue-dark)" }}>
                  {/* Card price for line is not shown per-line when discounts change it via recalc;
                      the cart totals show the correct card total after dual-pricing recomputation */}
                  ${(Number(line.cash_price_at_sale) * line.qty - Number(line.discount_applied)).toFixed(2)}
                </td>
                <td className="px-1 py-2.5">
                  <div className="flex items-center gap-0.5">
                    {isManager && onLineDiscount && (
                      <button
                        onClick={() => setDiscountLine(line)}
                        className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all"
                        style={{
                          color: hasDiscount ? "#fff" : "var(--rbw-gold)",
                          background: hasDiscount ? "var(--rbw-danger)" : "transparent",
                        }}
                        onMouseEnter={(e) => {
                          if (!hasDiscount) (e.currentTarget as HTMLElement).style.background = "var(--rbw-navy)";
                        }}
                        onMouseLeave={(e) => {
                          if (!hasDiscount) (e.currentTarget as HTMLElement).style.background = "transparent";
                        }}
                        title={hasDiscount ? "Edit discount" : "Apply discount"}
                      >
                        %
                      </button>
                    )}
                    <button
                      onClick={() => onRemove(line.id)}
                      className="w-7 h-7 rounded-full flex items-center justify-center text-sm transition-all"
                      style={{ color: "var(--rbw-gray-dark)" }}
                      onMouseEnter={(e) => {
                        (e.target as HTMLElement).style.color = "var(--rbw-danger)";
                        (e.target as HTMLElement).style.background = "#FEE2E2";
                      }}
                      onMouseLeave={(e) => {
                        (e.target as HTMLElement).style.color = "var(--rbw-gray-dark)";
                        (e.target as HTMLElement).style.background = "transparent";
                      }}
                      title="Remove"
                    >
                      &times;
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
